import { Dialog, Transition } from '@headlessui/react';
import Brain from 'assets/icons/Brain';
import Right from 'assets/icons/Right';
import Button from 'common/components/Button';
import Loader from 'common/components/Loader';
import { useToast } from 'common/hooks/useToast';
import useTranslation from 'common/hooks/useTranslation';
import { useResetSubmissionsAIInfoMutation } from 'common/slices/offerApi.slice';
import { cn } from 'common/utils';
import React, { Fragment } from 'react';

const GenerateAIModal = ({
  offerSlug,
  isOpen,
  setIsOpen,
  setResettingSubmissions,
}) => {
  const toast = useToast();
  const { t } = useTranslation('offers\\details');

  const [resetSubmissionsAIInfo, { isLoading }] =
    useResetSubmissionsAIInfoMutation();

  const handleResetSubmissionsAIInfo = async () => {
    setResettingSubmissions(true);
    try {
      await resetSubmissionsAIInfo({ offerSlug });
      toast.newToast('positive', t('toasts.success-ai-review'));
    } catch (err) {
      setResettingSubmissions(false);
      if (err?.status === 403) {
        toast.newToast('negative', t('toasts.forbidden'));
      } else {
        toast.newToast('negative', t('toasts.something-wrong'));
      }
    } finally {
      closeModal();
    }
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  return (
    <Fragment>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as='div'
          className='relative z-50'
          onClose={isLoading ? () => null : closeModal}
        >
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <div className='fixed inset-0 bg-black bg-opacity-25' />
          </Transition.Child>
          <div className='fixed inset-0 overflow-y-auto'>
            <div className='flex min-h-full items-center justify-center p-4 text-center'>
              <Transition.Child
                as={Fragment}
                enter='ease-out duration-300'
                enterFrom='opacity-0 scale-95'
                enterTo='opacity-100 scale-100'
                leave='ease-in duration-200'
                leaveFrom='opacity-100 scale-100'
                leaveTo='opacity-0 scale-95'
              >
                <Dialog.Panel
                  className={cn(
                    'flex flex-col w-full max-w-md transform overflow-hidden',
                    'rounded bg-white p-10 text-left gap-6',
                    'align-middle shadow-xl transition-all relative',
                  )}
                >
                  <Dialog.Title
                    as='div'
                    className='flex flex-col w-full items-center gap-8'
                  >
                    <Brain size='64px' color='#084D6E' />
                    <h1 className=' text-2xl font-semibold text-dark-charcoal'>
                      {t('modals.generate-ai.title')}
                    </h1>
                  </Dialog.Title>
                  <Dialog.Description className='text-sm text-cool-grey text-center'>
                    {t('modals.generate-ai.description')}
                  </Dialog.Description>
                  <div className='w-full flex items-center justify-center gap-2'>
                    <Button variant='secondary' onClick={closeModal}>
                      {t('modals.generate-ai.buttons.cancel')}
                    </Button>
                    <Button
                      variant='primary'
                      onClick={handleResetSubmissionsAIInfo}
                    >
                      {t('modals.generate-ai.buttons.continue')}
                      <Right size='16px' color='#FFFFFF' />
                    </Button>
                  </div>
                  {isLoading && <Loader />}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </Fragment>
  );
};

export default GenerateAIModal;
